import { CheckCircle2, Info, TriangleAlert, X, XCircle } from "lucide-react";
import * as React from "react";
import { create } from "zustand";

import { cn } from "./cn";

export type ToastVariant = "success" | "error" | "warning" | "info";

export interface Toast {
  id: number;
  title: string;
  description?: string;
  variant: ToastVariant;
}

interface ToastState {
  toasts: Toast[];
  push: (toast: Omit<Toast, "id">) => number;
  dismiss: (id: number) => void;
}

let nextId = 1;

const useToastStore = create<ToastState>((set) => ({
  toasts: [],
  push: (toast) => {
    const id = nextId++;
    set((state) => ({ toasts: [...state.toasts, { ...toast, id }] }));
    return id;
  },
  dismiss: (id) => set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) })),
}));

const AUTO_DISMISS_MS = 4500;

/** Imperative API usable outside React (error handlers, interceptors). */
export function toast(options: {
  title: string;
  description?: string;
  variant?: ToastVariant;
  duration?: number;
}): void {
  const { push, dismiss } = useToastStore.getState();
  const id = push({
    title: options.title,
    description: options.description,
    variant: options.variant ?? "info",
  });
  window.setTimeout(() => dismiss(id), options.duration ?? AUTO_DISMISS_MS);
}

const ICONS: Record<ToastVariant, React.ComponentType<{ className?: string }>> = {
  success: CheckCircle2,
  error: XCircle,
  warning: TriangleAlert,
  info: Info,
};

const VARIANT_CLASS: Record<ToastVariant, string> = {
  success: "text-success",
  error: "text-danger",
  warning: "text-warning",
  info: "text-brand",
};

export function Toaster() {
  const toasts = useToastStore((state) => state.toasts);
  const dismiss = useToastStore((state) => state.dismiss);

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-4 z-[100] flex flex-col items-center gap-2 px-4 sm:inset-x-auto sm:right-4 sm:items-end">
      {toasts.map((item) => {
        const Icon = ICONS[item.variant];
        return (
          <div
            key={item.id}
            role="status"
            className={cn(
              "pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-card border border-border bg-surface p-3 shadow-lg",
            )}
          >
            <Icon className={cn("mt-0.5 h-4 w-4 shrink-0", VARIANT_CLASS[item.variant])} />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-foreground">{item.title}</p>
              {item.description ? (
                <p className="mt-0.5 text-xs text-muted">{item.description}</p>
              ) : null}
            </div>
            <button
              type="button"
              onClick={() => dismiss(item.id)}
              className="rounded-md p-1 text-muted transition-colors hover:bg-surface-muted"
              aria-label="Dismiss"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        );
      })}
    </div>
  );
}
